import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { api, formatApiError } from "@/lib/api";
import { Mail, ArrowLeft } from "lucide-react";
import { useTurnstile, TurnstileMount } from "@/components/TurnstileWidget";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const turnstile = useTurnstile();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", {
        email,
        turnstile_token: turnstile.token,
        _hp_website: honeypot,
      });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      turnstile.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-14">
      <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#B31E1E] mb-6">
        <ArrowLeft size={14} /> Back to log in
      </Link>
      <Card className="rounded-[24px] p-8 border border-border" data-testid="forgot-password-card">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={18} className="text-[#B31E1E]" />
          <div className="overline text-gray-500">Password reset</div>
        </div>
        <h1 className="font-display text-3xl tracking-tight">Forgot your password?</h1>
        <p className="mt-2 text-gray-600 text-sm leading-relaxed">
          Enter the email tied to your Servrevu account and we'll send a link to choose a new password. The link is valid for 1 hour.
        </p>

        {sent ? (
          <div className="mt-6 p-4 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-900" data-testid="forgot-password-sent">
            If that email is registered with Servrevu, a reset link is on the way. Check your inbox (and spam folder).
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="fp-email">Email</Label>
              <Input
                id="fp-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="rounded-md mt-1"
                data-testid="forgot-password-email"
              />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="forgot-password-error">{error}</p>}
            <input
              type="text"
              name="_hp_website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
              data-testid="forgot-password-honeypot"
            />
            <TurnstileMount widgetRef={turnstile.widgetRef} sitekey={turnstile.sitekey} />
            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] h-11"
              data-testid="forgot-password-submit"
            >
              {submitting ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
